import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useContextMenu } from 'react-contexify';
import html2canvas from 'html2canvas';
import { computeDepartureTotals } from '../utils/departureTotals';
import { COLUMN_MENU_ID } from '../constants';
import { useDoubleTap } from '../hooks/useDoubleTap';
import { SortableItem } from './SortableItem';

export function Column({ id, column, allItems, canEdit, setCtxMenu }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const columnRef = React.useRef(null);
  const { show } = useContextMenu({ id: COLUMN_MENU_ID });

  const departureTotals = React.useMemo(() => {
    return computeDepartureTotals(column.items);
  }, [column.items]);

  const handleCapture = React.useCallback(async () => {
    const el = columnRef.current;
    if (!el) return;

    try {
      const canvas = await html2canvas(el, {
        backgroundColor: '#f5f5f5',
        scale: 2,
        useCORS: true
      });

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${column.name.replace(/\s+/g, '_')}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (err) {
      console.error('Erreur capture:', err);
      alert('Erreur lors de la capture');
    }
  }, [column.name]);

  const showHeaderMenu = React.useCallback((e) => {
    show({
      event: e.nativeEvent || e,
      props: { columnId: id, onCapture: handleCapture }
    });
  }, [show, id, handleCapture]);

  const handleHeaderContextMenu = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    showHeaderMenu(e);
  }, [showHeaderMenu]);

  const doubleTapHandlers = useDoubleTap(showHeaderMenu);

  const dynamicStyle = {
    background: isOver ? '#2c3e30' : undefined
  };

  return (
    <div ref={(node) => { setNodeRef(node); columnRef.current = node; }} className="column" style={dynamicStyle}>
      <h3
        style={{ textAlign: 'center', margin: '0 0 8px', cursor: 'context-menu' }}
        onContextMenu={handleHeaderContextMenu}
        {...doubleTapHandlers}
      >
        {column.name}
        {column.items.length > 0 &&
          (() => {
            const source = allItems || column.items;
            const realItems = source.filter((i) => i.type !== 'departure');

            if (realItems.length === 0) return null;

            const total = realItems.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
            const formatted = total.toFixed(2).replace('.', ',');

            return (
              <>
                {' '}
                ({realItems.length}) ({formatted} kg)
              </>
            );
          })()}
      </h3>

      {column.items.map((item) => (
        <SortableItem
          key={item.id}
          item={item}
          container={id}
          canEdit={canEdit}
          departureTotalKg={departureTotals[String(item.id)]}
          setCtxMenu={setCtxMenu}
        />
      ))}
    </div>
  );
}
